import { ServiceBooking } from '@turizi/shared';
import { Tooltip } from '@mantine/core';
import ShowSelfAttrs from './show-self-attrs';
import ProviderSelector from './service-booking/provider-selector';
import { formatPrice, formatExchangeRate } from '@/lib/format-utils';
import { t } from 'i18next';
import { IconGift, IconHandClick } from '@tabler/icons-react';
import ServiceDatePopover from './service-date-popover';
import SelectTimePopover from './select-time-popover';
import { ServiceTitle, ServiceTitleStatic } from './services/service-title';
import BookingServiceOrders from './service-booking/booking-service-orders';
import { TagIcon } from '@/lib/tag-utils';
import { Reservation } from '@turizi/shared';
import CoordinatorSelector from './service-booking/coordinator-selector';
import { BookingGroupSize } from './snippets';

interface Props {
    booking: ServiceBooking;
    reservation?: Reservation;
    selected?: boolean;
    onToggle?: (id: number) => void;
    buttonLabel?: string;
    onSelectBooking?: () => void; // Legacy/Unused?
    onSelect?: (booking: ServiceBooking) => void;
    showServiceTitle?: boolean;
    showServiceOrder?: boolean;
    showReservation?: boolean;
    showClientName?: boolean;
    showPaxs?: boolean;
    showPaxName?: boolean;
    showHour?: boolean;
    showPrice?: boolean;
    showCost?: boolean;
    showPaymentStatus?: boolean;
    firstTd?: React.ReactNode;
    leftAction?: (booking: ServiceBooking) => void;
    leftSection?: React.ReactNode;
    rightAction?: (booking: ServiceBooking) => void;
    rightSection?: React.ReactNode | ((booking: ServiceBooking) => React.ReactNode);
    editable?: boolean;
    hideServicePackage?: boolean;
    slim?: boolean;
}


export default function EditReservationBookingRow({
    booking,
    reservation,
    showCost = false,
    leftAction,
    hideServicePackage = true,
    slim = false,
}: Props): JSX.Element {
    const service = booking.service_data;
    const isPackage = service.child_services?.length > 0;

    const origCostAmount = booking?.cost_info?.money_out_amount;
    const origCostCurrency = booking?.cost_info?.money_out_currency;
    const currentCostAmount = booking?.money_out_amount;
    const currentCostCurrency = booking?.money_out_currency;

    const isCostManual = Boolean(
        booking?.cost_info &&
        (origCostAmount !== currentCostAmount || origCostCurrency !== currentCostCurrency)
    );

    const isCostSameCurrency = !origCostCurrency || !currentCostCurrency || origCostCurrency === currentCostCurrency;
    const isCostDifferentCurrency = Boolean(origCostCurrency && currentCostCurrency && origCostCurrency !== currentCostCurrency);

    let costPercentDiff: number | null = null;
    let costExchangeRate: number | null = null;

    if (isCostManual && origCostAmount != null && currentCostAmount != null && origCostAmount > 0) {
        if (isCostSameCurrency) {
            costPercentDiff = ((currentCostAmount - origCostAmount) / origCostAmount) * 100;
        } else if (isCostDifferentCurrency) {
            costExchangeRate = currentCostAmount / origCostAmount;
        }
    }

    const origPriceAmount = booking?.price_info?.money_in_amount;
    const origPriceCurrency = booking?.price_info?.money_in_currency;
    const currentPriceAmount = booking?.money_in_amount;
    const currentPriceCurrency = booking?.money_in_currency;

    const isPriceManual = Boolean(
        booking?.price_info &&
        (origPriceAmount !== currentPriceAmount || origPriceCurrency !== currentPriceCurrency)
    );

    const isPriceSameCurrency = !origPriceCurrency || !currentPriceCurrency || origPriceCurrency === currentPriceCurrency;
    const isPriceDifferentCurrency = Boolean(origPriceCurrency && currentPriceCurrency && origPriceCurrency !== currentPriceCurrency);

    let pricePercentDiff: number | null = null;
    let priceExchangeRate: number | null = null;

    if (isPriceManual && origPriceAmount != null && currentPriceAmount != null && origPriceAmount > 0) {
        if (isPriceSameCurrency) {
            pricePercentDiff = ((currentPriceAmount - origPriceAmount) / origPriceAmount) * 100;
        } else if (isPriceDifferentCurrency) {
            priceExchangeRate = currentPriceAmount / origPriceAmount;
        }
    }

    if (hideServicePackage && isPackage) {
        return null;
    }

    return (
        <div
            className='data-card-hover'
            onClick={(e) => {
                e.stopPropagation();
                leftAction?.(booking);
            }}
        >
            <div className="flex flex-col gap-1">
                {/* Top Row: Checkbox, Service Title, Compact Date/Hour popovers, Actions */}
                <div className="flex flex-col md:flex-row items-start justify-between gap-x-4 gap-y-2 border-b border-gray-100 pb-1">
                    <div className="flex items-start gap-3 sm:gap-6 min-w-0 cursor-pointer">
                        {/* Date/Time */}
                        {!isPackage && (
                            <div className="flex items-start gap-2">
                                <ServiceDatePopover
                                    icon={true}
                                    size='xs'
                                    serviceCategory={service.category}
                                    value={[booking.start_day, booking.end_day]}
                                    onChange={() => { }}
                                    className='font-bold text-gray-800'
                                    disabled={true}
                                />
                                <SelectTimePopover
                                    icon={true}
                                    size='xs'
                                    value={booking.start_hour}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </div>
                        )}
                        <div className='flex items-start gap-1 text-xs min-w-0 font-bold'>
                            <TagIcon
                                name={service.category}
                                size='xs' hideText noBackground={slim} className="shrink-0 mt-0.5" />
                            <ServiceTitle service={service} hideProviders />
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
                        {/* Coordinator / Service Orders */}
                        {
                            !isPackage
                                ? (
                                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
                                        <ProviderSelector booking={booking} size="xs" editable={false} />
                                        <CoordinatorSelector booking={booking} size='xs' editable={false} />
                                        <BookingServiceOrders booking={booking} editable={false} />
                                        <div className="text-xxs font-bold text-gray-400 shrink-0">
                                            #{booking.id}
                                        </div>
                                    </div>
                                )
                                :
                                (
                                    <div className="text-xxs font-bold text-gray-400 shrink-0">
                                        #{booking.id}
                                    </div>
                                )
                        }
                    </div>
                </div>

                <div className='sm:pl-[140px] flex flex-col lg:flex-row gap-2 items-start lg:items-stretch justify-between min-w-0 pt-1 w-full'>
                    <div className="flex flex-wrap items-start gap-2 text-xs min-w-0 flex-1">
                        <BookingGroupSize
                            booking={booking}
                            reservation={reservation}
                        />
                        {/* details*/}
                        {!isPackage ? (
                            <>
                                {/* Details (Attributes) */}
                                <ShowSelfAttrs
                                    showkey={false}
                                    className='flex flex-wrap gap-1 min-w-0 max-w-full'
                                    attrs={booking.self_attrs}
                                    context={booking}
                                    size="xs"
                                />
                                {/* Observations */}
                                {booking.observations && (
                                    <div className='text-xs px-2 py-0.5 bg-red-50/50 text-gray-800 rounded-lg border border-red-100 max-w-full'>
                                        {booking.observations}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className='flex gap-1 flex-wrap text-xxs font-light min-w-0'>
                                {service.child_services.map(child => (
                                    <div key={child.id} className="text-slate-800 font-light bg-gray-100 rounded-full px-2 py-0.5">
                                        {ServiceTitleStatic(child)} <span className='ml-2 font-bold text-gray-400'>#{child.id}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* prices */}
                    <div className="self-end mt-auto flex flex-wrap items-end justify-start lg:justify-end gap-3 text-xs w-full lg:w-auto pt-2 lg:pt-0 border-t lg:border-t-0 border-gray-100 shrink-0">
                        {/* Cost section */}
                        {showCost && (
                            <div className="flex flex-col items-start lg:items-end min-w-0">
                                <div className="flex items-baseline gap-1 whitespace-nowrap">
                                    <span className="text-[10px] text-red-300 uppercase font-normal">{t('cost')}:</span>
                                    <span className="text-red-750 font-extrabold">${formatPrice(booking.money_out_amount)}</span>
                                    <span>{booking.money_out_currency}</span>
                                    {isCostManual && (
                                        <Tooltip
                                            withArrow
                                            multiline
                                            w={220}
                                            label={
                                                <div className="text-xs space-y-0.5">
                                                    <div className="font-semibold">
                                                        {t('original_cost')}: ${formatPrice(origCostAmount)} {origCostCurrency}
                                                    </div>
                                                    {isCostSameCurrency && costPercentDiff != null && (
                                                        <div>
                                                            {costPercentDiff > 0 ? `+${costPercentDiff.toFixed(1)}%` : `${costPercentDiff.toFixed(1)}%`}
                                                        </div>
                                                    )}
                                                    {isCostDifferentCurrency && costExchangeRate != null && (
                                                        <div>
                                                            1 {origCostCurrency} = {formatExchangeRate(costExchangeRate)} {currentCostCurrency}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <span className="text-amber-600 cursor-help inline-flex items-center ml-0.5">
                                                <IconHandClick size={12} stroke={2.5} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                {isCostManual && origCostAmount != null && (
                                    <div className="text-[10px] text-gray-400 flex flex-wrap items-center gap-1 justify-start lg:justify-end">
                                        <span>${formatPrice(origCostAmount)} {origCostCurrency}</span>
                                        {isCostSameCurrency && costPercentDiff != null && (
                                            <span className={`font-semibold ${costPercentDiff > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                ({costPercentDiff > 0 ? `+${costPercentDiff.toFixed(1).replace(/\.0$/, '')}%` : `${costPercentDiff.toFixed(1).replace(/\.0$/, '')}%`})
                                            </span>
                                        )}
                                        {isCostDifferentCurrency && costExchangeRate != null && (
                                            <span className="text-blue-600 font-semibold">
                                                (1 {origCostCurrency} = {formatExchangeRate(costExchangeRate)} {currentCostCurrency})
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Price section */}
                        {booking.parent_booking_id ? (
                            <div className='flex items-center gap-1.5 text-gray-300 text-xxs font-bold'>
                                <IconGift size={12} stroke={2} />
                                #{booking.parent_booking_id}
                            </div>
                        ) : (
                            <div className="flex flex-col items-start lg:items-end min-w-0">
                                <div className="flex items-baseline gap-1 whitespace-nowrap">
                                    <span className="text-[10px] text-gray-500 uppercase font-normal">{t('price')}:</span>
                                    <span className="text-gray-800 font-extrabold">${formatPrice(booking.money_in_amount)}</span>
                                    <span>{booking.money_in_currency}</span>
                                    {isPriceManual && (
                                        <Tooltip
                                            withArrow
                                            multiline
                                            w={220}
                                            label={
                                                <div className="text-xs space-y-0.5">
                                                    <div className="font-semibold">
                                                        {t('original_price')}: ${formatPrice(origPriceAmount)} {origPriceCurrency}
                                                    </div>
                                                    {booking.manual_price_description && (
                                                        <div className="italic text-gray-200">{booking.manual_price_description}</div>
                                                    )}
                                                    {isPriceSameCurrency && pricePercentDiff != null && (
                                                        <div>
                                                            {pricePercentDiff > 0 ? `+${pricePercentDiff.toFixed(1)}%` : `${pricePercentDiff.toFixed(1)}%`}
                                                        </div>
                                                    )}
                                                    {isPriceDifferentCurrency && priceExchangeRate != null && (
                                                        <div>
                                                            1 {origPriceCurrency} = {formatExchangeRate(priceExchangeRate)} {currentPriceCurrency}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <span className="text-amber-600 cursor-help inline-flex items-center ml-0.5">
                                                <IconHandClick size={12} stroke={2.5} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                {isPriceManual && origPriceAmount != null && (
                                    <div className="text-[10px] text-gray-400 flex flex-wrap items-center gap-1 justify-start lg:justify-end">
                                        <span>${formatPrice(origPriceAmount)} {origPriceCurrency}</span>
                                        {isPriceSameCurrency && pricePercentDiff != null && (
                                            <span className={`font-semibold ${pricePercentDiff > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                ({pricePercentDiff > 0 ? `+${pricePercentDiff.toFixed(1).replace(/\.0$/, '')}%` : `${pricePercentDiff.toFixed(1).replace(/\.0$/, '')}%`})
                                            </span>
                                        )}
                                        {isPriceDifferentCurrency && priceExchangeRate != null && (
                                            <span className="text-blue-600 font-semibold">
                                                (1 {origPriceCurrency} = {formatExchangeRate(priceExchangeRate)} {currentPriceCurrency})
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div >
    );
}

